import { useEffect, useMemo, useState } from 'react';
import type { ConversationListEntry, WorkspaceUser } from '../../types';
import { api, ApiError } from '../../api';
import { useChat } from '../../store';
import { Avatar } from '../Avatar';
import { confirmDialog } from '../ConfirmHost';
import { IconMoreVertical, IconPlus, IconSearch } from '../Icon';

interface InfoMembersProps {
  conversation: ConversationListEntry;
  onLeft: () => void;
}

export function InfoMembers({ conversation, onLeft }: InfoMembersProps) {
  const me = useChat((s) => s.me);
  const users = useChat((s) => s.users);
  const onlineUserIds = useChat((s) => s.onlineUserIds);
  const upsertConversation = useChat((s) => s.upsertConversation);
  const selectConversation = useChat((s) => s.selectConversation);
  // Owners and admins both have admin powers — the owner tag only
  // protects against demotion, not the action permissions.
  const isAdmin =
    conversation.myRole === 'room_admin' || conversation.myRole === 'room_owner';
  const isOwner = conversation.myRole === 'room_owner';

  const [showSearch, setShowSearch] = useState(false);
  const [filter, setFilter] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [addQuery, setAddQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);

  // Close the row menu on any outside click.
  useEffect(() => {
    if (!openMenu) return;
    const close = () => setOpenMenu(null);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [openMenu]);

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return conversation.participants;
    return conversation.participants.filter((p) => {
      const name = (p.displayName ?? '').toLowerCase();
      const email = p.email.toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [filter, conversation.participants]);

  const memberIds = useMemo(
    () => new Set(conversation.participants.map((p) => p.id)),
    [conversation.participants],
  );

  const addable: WorkspaceUser[] = useMemo(() => {
    const q = addQuery.trim().toLowerCase();
    return users
      .filter((u) => u.id !== me?.id && !memberIds.has(u.id))
      .filter((u) =>
        !q
          ? true
          : u.email.toLowerCase().includes(q) ||
            (u.displayName ?? '').toLowerCase().includes(q),
      )
      .slice(0, 30);
  }, [users, me?.id, memberIds, addQuery]);

  async function addMember(userId: string) {
    setBusy(true);
    setError(null);
    try {
      await api.addConversationMember(conversation.id, userId);
      setAddQuery('');
      setShowAdd(false);
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 403
          ? 'Only admins can add members.'
          : 'Could not add member.',
      );
    } finally {
      setBusy(false);
    }
  }

  async function removeMember(userId: string) {
    setOpenMenu(null);
    const isSelf = userId === me?.id;
    const ok = await confirmDialog({
      title: isSelf ? 'Leave this conversation?' : 'Remove this member?',
      description: isSelf
        ? 'You will stop receiving messages and can be re-added by an admin.'
        : 'They lose access immediately. Past messages they sent stay in the chat history.',
      confirmLabel: isSelf ? 'Leave' : 'Remove',
      destructive: true,
    });
    if (!ok) return;
    setError(null);
    try {
      await api.removeConversationMember(conversation.id, userId);
      if (userId === me?.id) onLeft();
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 403
          ? 'Only admins can remove members.'
          : 'Could not remove member.',
      );
    }
  }

  async function setRole(userId: string, role: 'room_admin' | 'member') {
    setOpenMenu(null);
    setError(null);
    try {
      await api.updateConversationMemberRole(conversation.id, userId, role);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'last_admin') {
        setError("Can't demote the last admin — promote someone else first.");
      } else if (err instanceof ApiError && err.status === 403) {
        setError('Only admins can change member roles.');
      } else {
        setError('Could not update the role.');
      }
    }
  }

  async function startDm(userId: string) {
    setOpenMenu(null);
    setError(null);
    try {
      const res = await api.createDm(userId);
      // Insert at the top so the conversation list reflects the new DM and
      // selecting it works without a round-trip to the list endpoint.
      upsertConversation({
        ...res.conversation,
        lastMessage: null,
        unreadCount: 0,
        lastReadId: null,
        myRole: 'member',
        isFavorite: false,
        archivedAt: null,
        mutedUntil: null,
        channelId: null,
      });
      selectConversation(res.conversation.id);
    } catch {
      setError('Could not start a DM.');
    }
  }

  return (
    <div className="px-4 py-4 space-y-4">
      {error && (
        <p className="text-xs text-rose-400 bg-rose-500/10 border border-rose-500/30 rounded-md px-3 py-2">
          {error}
        </p>
      )}

      {/* Toolbar — search + add */}
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] uppercase tracking-wide text-zinc-500">
          {conversation.participants.length} members
        </p>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setShowSearch((v) => !v)}
            className="p-1.5 rounded-md text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900 transition"
            aria-label="Search members"
          >
            <IconSearch size={14} />
          </button>
          {isAdmin && (
            <button
              onClick={() => setShowAdd((v) => !v)}
              className="p-1.5 rounded-md text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900 transition"
              aria-label="Add member"
            >
              <IconPlus size={14} />
            </button>
          )}
        </div>
      </div>

      {showSearch && (
        <input
          autoFocus
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Search members…"
          className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      )}

      {showAdd && isAdmin && (
        <div className="rounded-md border border-zinc-800 bg-zinc-900 p-2 space-y-1">
          <input
            autoFocus
            type="text"
            value={addQuery}
            onChange={(e) => setAddQuery(e.target.value)}
            placeholder="Add teammate by name or email…"
            className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <ul className="max-h-56 overflow-y-auto">
            {addable.length === 0 && (
              <p className="text-[11px] text-zinc-500 px-2 py-2">
                {addQuery ? 'No matches.' : 'Everyone is already in this group.'}
              </p>
            )}
            {addable.map((u) => (
              <li key={u.id}>
                <button
                  onClick={() => addMember(u.id)}
                  disabled={busy}
                  className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-zinc-800 transition disabled:opacity-50 text-left"
                >
                  <Avatar id={u.id} displayName={u.displayName} email={u.email} size={24} />
                  <span className="text-xs truncate">{u.displayName ?? u.email}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Member list */}
      <ul className="space-y-0.5">
        {visible.map((p) => {
          const isMe = p.id === me?.id;
          const online = onlineUserIds[p.id] ?? false;
          // Source-of-truth for the role: the participant entry's own
          // role field. For the current user, we fall back to
          // `conversation.myRole` when p.role is missing — protects
          // against any code path that populates participants without
          // the role column (e.g. a stale conversations-list payload
          // from before the role-included fix).
          const effectiveRole =
            p.role ?? (isMe ? conversation.myRole : undefined);
          const isMemberOwner = effectiveRole === 'room_owner';
          const isMemberAdmin = effectiveRole === 'room_admin' || isMemberOwner;
          const menuOpen = openMenu === p.id;
          const showMenuButton =
            (isAdmin && !isMe) || (!isAdmin && !isMe) || (isMe && conversation.type !== 'dm');
          return (
            <li
              key={p.id}
              className="relative flex items-center gap-3 px-2 py-2 rounded-md hover:bg-zinc-900/50 transition group"
            >
              <Avatar
                id={p.id}
                displayName={p.displayName}
                email={p.email}
                online={online}
                size={36}
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm truncate">
                  {p.displayName ?? p.email}
                  {isMe && <span className="text-[10px] text-zinc-500 ml-1.5">(you)</span>}
                </p>
                <p className="text-[11px] text-zinc-500 truncate">{p.email}</p>
              </div>
              {isMemberOwner ? (
                <span className="text-[10px] uppercase tracking-wide text-amber-300 shrink-0">
                  Owner
                </span>
              ) : isMemberAdmin ? (
                <span className="text-[10px] uppercase tracking-wide text-indigo-400 shrink-0">
                  Group admin
                </span>
              ) : null}
              {showMenuButton && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenMenu(menuOpen ? null : p.id);
                  }}
                  className={`shrink-0 p-1 rounded-md text-zinc-500 hover:text-zinc-100 hover:bg-zinc-900 transition ${
                    menuOpen ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                  }`}
                  aria-label="Member actions"
                  aria-expanded={menuOpen}
                >
                  <IconMoreVertical size={14} />
                </button>
              )}
              {menuOpen && (
                <div
                  onClick={(e) => e.stopPropagation()}
                  className="absolute right-2 top-12 z-10 w-44 rounded-md border border-zinc-800 bg-zinc-950 shadow-lg overflow-hidden text-xs"
                >
                  {!isMe && (
                    <MenuItem onClick={() => void startDm(p.id)}>Send DM</MenuItem>
                  )}
                  {/* Owner can't be promoted (they already have admin
                      powers) or demoted by anyone else; hide both
                      role-change options when the target is the owner. */}
                  {!isMe && isAdmin && !isMemberAdmin && (
                    <MenuItem onClick={() => void setRole(p.id, 'room_admin')}>
                      Make admin
                    </MenuItem>
                  )}
                  {!isMe && isAdmin && isMemberAdmin && !isMemberOwner && (
                    <MenuItem onClick={() => void setRole(p.id, 'member')}>
                      Remove admin
                    </MenuItem>
                  )}
                  {/* Server also enforces this — admins can't remove the
                      owner. Hiding the menu item is a UX nicety; the
                      action would 409 anyway. */}
                  {!isMe && isAdmin && !isMemberOwner && (
                    <MenuItem
                      onClick={() => void removeMember(p.id)}
                      tone="danger"
                    >
                      Remove from group
                    </MenuItem>
                  )}
                  {isMe && conversation.type !== 'dm' && (
                    <MenuItem
                      onClick={() => void removeMember(p.id)}
                      tone="danger"
                    >
                      Leave group
                    </MenuItem>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function MenuItem({
  children,
  onClick,
  tone = 'default',
}: {
  children: React.ReactNode;
  onClick: () => void;
  tone?: 'default' | 'danger';
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-3 py-2 transition ${
        tone === 'danger'
          ? 'text-rose-400 hover:bg-rose-500/10'
          : 'text-zinc-200 hover:bg-zinc-900'
      }`}
    >
      {children}
    </button>
  );
}
